
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../utils/auth';
import { getPatients, addVital, addLog, getPatientVitals, addEditRequest } from '../../utils/storage';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import type { Patient, VitalReading } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

export default function BPEntry() {
  const navigate = useNavigate();
  const user = getUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [alreadyRecorded, setAlreadyRecorded] = useState(false);
  const [existingVital, setExistingVital] = useState<VitalReading | null>(null);

  // Update request modal
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newSystolic, setNewSystolic] = useState('');
  const [newDiastolic, setNewDiastolic] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/dashboard'); return; }
    if (user.role !== 'bp' && user.role !== 'admin' && user.role !== 'master_admin' && user.role !== 'all_vitals') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user) return null;

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    setSelectedPatient(null);
    setAlreadyRecorded(false);
    setExistingVital(null);
    if (value.trim().length > 0) {
      const patients = await getPatients();
      const results = patients.filter(
        (p) =>
          p.id.toLowerCase().includes(value.toLowerCase()) ||
          p.name.toLowerCase().includes(value.toLowerCase()) ||
          p.contactNumber.includes(value)
      );
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const selectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.name} (${patient.id})`);
    setShowResults(false);
    const patientVitals = await getPatientVitals(patient.id);
    const bpVital = patientVitals.find(v => v.type === 'bp');
    if (bpVital) {
      setAlreadyRecorded(true);
      setExistingVital(bpVital);
    } else {
      setAlreadyRecorded(false);
      setExistingVital(null);
    }
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedPatient) { setError('Please select a patient first.'); return; }
    if (alreadyRecorded) { setError('Blood Pressure already recorded. Use "Request Update" to change it.'); return; }
    if (!systolic || !diastolic) { setError('Please enter both systolic and diastolic values.'); return; }

    setLoading(true);
    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();

    addVital({
      id: `V${Date.now()}_BP`,
      patientId: selectedPatient.id,
      type: 'bp',
      value: `${systolic}/${diastolic}`,
      unit: 'mmHg',
      recordedAt: timestamp,
      recordedBy: user.username,
    });

    addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'BP Entry',
      details: `Recorded BP ${systolic}/${diastolic} mmHg for patient ${selectedPatient.name} (${selectedPatient.id})`,
      ipAddress, deviceName: device, browser, timestamp,
    });

    setSuccess(true);
    setLoading(false);
    setTimeout(() => {
      setSystolic(''); setDiastolic(''); setSelectedPatient(null);
      setSearchTerm(''); setSuccess(false); setAlreadyRecorded(false); setExistingVital(null);
    }, 2000);
  };

  const handleUpdateRequest = async () => {
    if (!selectedPatient || !existingVital) return;
    if (!newSystolic || !newDiastolic) { setError('Please enter new BP values.'); return; }
    if (!updateReason.trim()) { setError('Please provide a reason for the update.'); return; }

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();

    addEditRequest({
      id: `ER${Date.now()}`,
      patientId: selectedPatient.id,
      patientName: selectedPatient.name,
      vitalId: existingVital.id,
      vitalType: 'bp',
      currentValue: existingVital.value,
      newValue: `${newSystolic}/${newDiastolic}`,
      requestedBy: user.username,
      requestedAt: timestamp,
      reason: updateReason,
      status: 'pending',
    });

    addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'Edit Request',
      details: `Requested BP update for ${selectedPatient.name} (${selectedPatient.id}): ${existingVital.value} → ${newSystolic}/${newDiastolic}`,
      ipAddress, deviceName: device, browser, timestamp,
    });

    setShowUpdateModal(false);
    setNewSystolic(''); setNewDiastolic(''); setUpdateReason('');
    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Blood Pressure Entry</h2>
          <p className="text-sm text-gray-500 mt-1">Search for a patient and record blood pressure reading</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {success && (
            <div className="mb-5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-500 text-base"></i>
              Blood pressure recorded successfully!
            </div>
          )}
          {updateSuccess && (
            <div className="mb-5 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-send-plane-line text-teal-500 text-base"></i>
              Update request submitted. Awaiting admin approval.
            </div>
          )}
          {error && (
            <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500 text-base"></i>
              {error}
            </div>
          )}

          {/* Patient Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Patient <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchTerm && setShowResults(true)}
                placeholder="Search by ID, Name, or Mobile Number"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
              />
            </div>
            {showResults && searchResults.length > 0 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto z-10 relative">
                {searchResults.map((patient) => (
                  <button key={patient.id} onClick={() => selectPatient(patient)}
                    className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-left cursor-pointer">
                    <p className="text-sm font-medium text-gray-800">{patient.name}</p>
                    <p className="text-xs text-gray-500">{patient.id} • {patient.contactNumber}</p>
                  </button>
                ))}
              </div>
            )}
            {showResults && searchResults.length === 0 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                <p className="text-sm text-gray-500">No patients found</p>
              </div>
            )}
          </div>

          {/* Selected Patient Info */}
          {selectedPatient && (
            <div className="mb-6 bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                <i className="ri-user-line text-teal-600"></i>
                Selected Patient
              </h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium text-gray-800">{selectedPatient.name}</span></div>
                <div><span className="text-gray-500">ID:</span> <span className="font-medium text-gray-800">{selectedPatient.id}</span></div>
                <div><span className="text-gray-500">Age:</span> <span className="font-medium text-gray-800">{selectedPatient.age} yrs</span></div>
                <div><span className="text-gray-500">Gender:</span> <span className="font-medium text-gray-800">{selectedPatient.gender}</span></div>
              </div>
              <div className="mt-3 pt-3 border-t border-teal-200">
                <span className="text-xs text-gray-500 mr-2">Payment:</span>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  selectedPatient.paymentStatus === 'paid'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  <i className={selectedPatient.paymentStatus === 'paid' ? 'ri-checkbox-circle-fill' : 'ri-time-line'}></i>
                  {selectedPatient.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
              </div>
            </div>
          )}

          {/* Already Recorded Banner */}
          {alreadyRecorded && existingVital && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-alert-line text-orange-500"></i>
                    <span className="text-sm font-semibold text-orange-800">BP Already Recorded</span>
                  </div>
                  <p className="text-sm text-orange-700">
                    Current value: <span className="font-bold">{existingVital.value} mmHg</span>
                  </p>
                  <p className="text-xs text-orange-600 mt-0.5">
                    Recorded by {existingVital.recordedBy} on {new Date(existingVital.recordedAt).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setShowUpdateModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line"></i>
                  Request Update
                </button>
              </div>
            </div>
          )}

          {/* BP Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-red-500 text-base"></i>
                </div>
                Blood Pressure Reading
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Systolic <span className="text-gray-400 font-normal">(mmHg)</span> <span className="text-red-500">*</span></label>
                  <input type="number" value={systolic} onChange={(e) => setSystolic(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="e.g. 120" min="50" max="300" disabled={alreadyRecorded} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Diastolic <span className="text-gray-400 font-normal">(mmHg)</span> <span className="text-red-500">*</span></label>
                  <input type="number" value={diastolic} onChange={(e) => setDiastolic(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="e.g. 80" min="30" max="200" disabled={alreadyRecorded} />
                </div>
              </div>
              {systolic && diastolic && !alreadyRecorded && (
                <div className="mt-3 text-sm text-teal-700 font-medium bg-teal-50 px-3 py-2 rounded-md">
                  Reading: {systolic}/{diastolic} mmHg
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={loading || !selectedPatient || alreadyRecorded}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm">
                {loading ? 'Recording...' : 'Record BP'}
              </button>
              <button type="button" onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Update Request Modal */}
      {showUpdateModal && existingVital && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">Request BP Update</h3>
              <button onClick={() => setShowUpdateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="mb-4 bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Current value:</span>
              <span className="ml-2 font-bold text-gray-800">{existingVital.value} mmHg</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">New Systolic (mmHg) <span className="text-red-500">*</span></label>
                <input type="number" value={newSystolic} onChange={(e) => setNewSystolic(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                  placeholder="e.g. 118" min="50" max="300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">New Diastolic (mmHg) <span className="text-red-500">*</span></label>
                <input type="number" value={newDiastolic} onChange={(e) => setNewDiastolic(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                  placeholder="e.g. 78" min="30" max="200" />
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Reason for Update <span className="text-red-500">*</span></label>
              <textarea value={updateReason} onChange={(e) => setUpdateReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm resize-none"
                rows={3} placeholder="Explain why this value needs to be updated..." maxLength={300} />
            </div>
            <div className="flex gap-3">
              <button onClick={handleUpdateRequest}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm">
                Submit Request
              </button>
              <button onClick={() => setShowUpdateModal(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
